/* eslint-disable react/jsx-props-no-spreading */
import { LocalizationProvider, DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import PropTypes from 'prop-types';
import logger from '../utils/logger';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Convert a naive date/time string (interpreted as America/New_York) to a UTC ISO string.
 * Used by SearchCriteria to convert picker output before storing in search state.
 *
 * @param {string} dateString - date/time string in `America/New_York` local time
 * @returns {string} UTC ISO string
 */
const convertToUtc = (dateString) => dayjs.tz(dateString, 'America/New_York').utc().toDate().toISOString();

function CustomDateRangePicker({ setCriteria, criteria }) {
  let start = dayjs();
  let end = dayjs();
  if (Array.isArray(criteria) && criteria.length === 2) {
    if (dayjs(criteria[0]).isValid()) start = dayjs(criteria[0]);
    if (dayjs(criteria[1]).isValid()) end = dayjs(criteria[1]);
  }
  const [datePicked, setDatePicked] = useState([start, end]);
  const updateDate = (newValue) => {
    setDatePicked(newValue);
    if (!newValue[0] || !newValue[1]) return;
    setCriteria([
      dayjs(newValue[0]).format('YYYY-MM-DD'),
      dayjs(newValue[1]).format('YYYY-MM-DD'),
    ]);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePicker
        startText="Check-in"
        endText="Check-out"
        value={datePicked}
        onChange={(newValue) => updateDate(newValue)}
        slotProps={{ textField: { variant: 'outlined' } }}
        sx={{ width: 275 }}
      />
    </LocalizationProvider>
  );
}

CustomDateRangePicker.propTypes = {
  setCriteria: PropTypes.func.isRequired,
  criteria: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
};

function CustomDatePicker({
  format, setCriteria, criteria, name = null,
}) {
  let start = dayjs();
  try {
    if (dayjs(criteria).isValid()) start = dayjs(criteria);
  } catch (err) {
    logger.error(err);
  }

  const [datePicked, setDatePicked] = useState(start);
  const updateDate = (newValue) => {
    setDatePicked(newValue);
    const newDate = dayjs(newValue).format('YYYY-MM-DD');
    if (name) {
      setCriteria(name, newDate);
    } else {
      setCriteria(newDate);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Date"
        value={datePicked}
        format={format}
        placeholder={format}
        onChange={(newValue) => updateDate(newValue)}
        sx={{ width: 275 }}
      />
    </LocalizationProvider>
  );
}

CustomDatePicker.propTypes = {
  format: PropTypes.string.isRequired,
  setCriteria: PropTypes.func.isRequired,
  criteria: PropTypes.string.isRequired,
  name: PropTypes.string,
};

function CustomDateTimeRangePicker({ setCriteria, criteria }) {
  let start = dayjs();
  let end = dayjs();
  if (Array.isArray(criteria) && criteria.length === 2) {
    if (dayjs(criteria[0]).isValid()) start = dayjs(criteria[0]);
    if (dayjs(criteria[1]).isValid()) end = dayjs(criteria[1]);
  }

  const [datePicked, setDatePicked] = useState([start, end]);
  const updateDate = (newValue, index) => {
    const newRange = [...datePicked];
    newRange[index] = newValue;
    setDatePicked(newRange);
    if (!newRange[0] || !newRange[1]) return;
    setCriteria([
      dayjs(newRange[0]).format('YYYY-MM-DDTHH:mm:ss'),
      dayjs(newRange[1]).format('YYYY-MM-DDTHH:mm:ss'),
    ]);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        label="Start"
        value={datePicked[0]}
        onChange={(newValue) => updateDate(newValue, 0)}
        sx={{ width: 225 }}
      />
      -
      <DateTimePicker
        label="End"
        value={datePicked[1]}
        onChange={(newValue) => updateDate(newValue, 1)}
        sx={{ width: 225 }}
      />
    </LocalizationProvider>
  );
}

CustomDateTimeRangePicker.propTypes = {
  setCriteria: PropTypes.func.isRequired,
  criteria: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
};

function CustomDateTimePicker({ setCriteria, criteria }) {
  let start = dayjs();
  try {
    if (dayjs(criteria).isValid()) start = dayjs(criteria);
  } catch (err) {
    logger.error(err);
  }

  const [datePicked, setDatePicked] = useState(start);
  const updateDate = (newValue) => {
    setDatePicked(newValue);
    setCriteria(dayjs(newValue).format('YYYY-MM-DDTHH:mm:ss'));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        label="Date"
        value={datePicked}
        onChange={updateDate}
        sx={{ width: 275 }}
      />
    </LocalizationProvider>
  );
}

CustomDateTimePicker.propTypes = {
  setCriteria: PropTypes.func.isRequired,
  criteria: PropTypes.string.isRequired,
};

export {
  CustomDateRangePicker,
  CustomDatePicker,
  CustomDateTimeRangePicker,
  CustomDateTimePicker,
  convertToUtc,
};
